



















































































import Vue from 'vue'
import Component from 'vue-class-component'
import { PaymentMethod } from '@/core/ApiTypes'
import appManager from '@/services/AppManager'
import * as api from '@/services/ApiService'
import Spinner from '@/components/Spinner.vue'
import { AppColors } from '@/model/AppColors'
import { isDateAfterYearMonth } from '@/core/DateUtils'
import { CardBrands } from '@/extern/StripeTypes'
import AddPaymentMethodDialog from '@/components/paymentmethods/AddPaymentMethodDialog.vue'

@Component({
    components: {
        Spinner,
        AddPaymentMethodDialog
    }
})
export default class PaymentMethodsView extends Vue {
    companyId: string|null = null

    isLoadingDefaultPaymentMethod: boolean = true
    defaultPaymentMethodId: string|null = null

    isLoadingPaymentMethods: boolean = false
    paymentMethods: PaymentMethod[] = []
    hasMorePaymentMethods: boolean = false

    // add payment method dialog
    isAddPaymentMethodDialogVisible: boolean = false
    addPaymentMethodDialogCounter: number = 0 // simple way to reset the dialog each time it is opened

    // noinspection JSUnusedGlobalSymbols
    created() {
        const user = appManager.userManager.state?.user
        if (user && user.data.companyId) {
            this.companyId = user.data.companyId

            if (this.companyId) {
                // asynchronously load these two at the same time
                this.loadDefaultPaymentMethod()
                this.loadMorePaymentMethods()
            }
        }
    }

    async loadDefaultPaymentMethod(): Promise<void> {
        if (!this.companyId)
            return

        this.isLoadingDefaultPaymentMethod = true
        try {
            const defaultPaymentMethod = await api.getDefaultPaymentMethod(this.companyId)
            this.defaultPaymentMethodId = defaultPaymentMethod?.id || null
        } catch (error) {
            console.error(`Error Loading Default Payment Method: ${error}`)
        }
        this.isLoadingDefaultPaymentMethod = false
    }

    async loadMorePaymentMethods(): Promise<void> {
        if (!this.companyId)
            return

        this.isLoadingPaymentMethods = true

        try {
            const response = await api.queryPaymentMethods({
                companyId: this.companyId,
                startAfterStripePaymentMethodId: (this.paymentMethods.length > 0) ? this.paymentMethods[this.paymentMethods.length-1].id : undefined
            })
            for (const paymentMethod of response.paymentMethods)
                this.paymentMethods.push(paymentMethod)
            this.hasMorePaymentMethods = response.hasMore
        } catch (error) {
            console.error(`Error Loading Payment Methods: ${error}`)
        }

        this.isLoadingPaymentMethods = false
    }

    isDefaultPaymentMethod(paymentMethod: PaymentMethod): boolean {
        return !!this.defaultPaymentMethodId && this.defaultPaymentMethodId === paymentMethod?.id
    }

    getPaymentMethodStatusColor(paymentMethod: PaymentMethod): string {
        const expireYear = paymentMethod?.data?.expireYear
        const expireMonth = paymentMethod?.data?.expireMonth
        let isExpired = true
        if (expireYear && expireMonth) {
            const now = new Date(Date.now())
            if (!isDateAfterYearMonth(now, expireYear, expireMonth))
                isExpired = false
        }

        if (isExpired)
            return AppColors.secondary

        return this.isDefaultPaymentMethod(paymentMethod) ? AppColors.accent : AppColors.info
    }

    getPaymentMethodCardImage(paymentMethod: PaymentMethod): string {
        const dir = '/images/credit_cards/light'

        switch (paymentMethod?.data?.cardBrand) {
            case CardBrands.amex: return `${dir}/amex.png`
            case CardBrands.diners: return `${dir}/diners.png`
            case CardBrands.discover: return `${dir}/discover.png`
            case CardBrands.jcb: return `${dir}/jcb.png`
            case CardBrands.mastercard: return `${dir}/mastercard.png`
            case CardBrands.visa: return `${dir}/visa.png`
            default: return `${dir}/unknown.png`
        }
    }

    getPaymentMethodCardBrandText(paymentMethod: PaymentMethod): string {
        const brand = paymentMethod?.data?.cardBrand
        return brand?.toUpperCase() || '---'
    }

    getPaymentMethodCardNumberText(paymentMethod: PaymentMethod): string {
        return paymentMethod?.data?.cardLast4 || "---"
    }

    getExpiresDateText(paymentMethod: PaymentMethod): string {
        const expireYear = paymentMethod?.data?.expireYear
        const expireMonth = paymentMethod?.data?.expireMonth
        if (!expireYear || !expireMonth)
            return "---"

        const yearText = expireYear.toString()
        const monthText = expireMonth.toString().padStart(2, '0')

        return `${monthText}/${yearText}`
    }

    onAddPaymentMethodPressed() {
        this.addPaymentMethodDialogCounter += 1
        this.isAddPaymentMethodDialogVisible = true
    }

    onCloseAddPaymentMethodDialogRequested(added?: boolean) {
        if (added) {
            this.paymentMethods = []
            this.loadDefaultPaymentMethod()
            this.loadMorePaymentMethods()
        }
        this.isAddPaymentMethodDialogVisible = false
    }

    async onSetDefaultPaymentMethodPressed(paymentMethod: PaymentMethod) {
        if (!this.companyId || !paymentMethod.id)
            return

        this.isLoadingDefaultPaymentMethod = true
        try {
            await api.setDefaultPaymentMethod({
                companyId: this.companyId,
                stripePaymentMethodId: paymentMethod.id
            })
            this.defaultPaymentMethodId = paymentMethod.id
        } catch (error) {
            console.error(`Error Setting Default Payment Method: ${error}`)
        }
        this.isLoadingDefaultPaymentMethod = false
    }
}
